import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, SectionHead } from "./Reveal";
import SceneTour from "./SceneTour";

const SHOWCASE_IMG =
    "https://images.unsplash.com/photo-1630703125102-ba2c078309d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxlcXVpcmVjdGFuZ3VsYXIlMjAzNjAlMjBwYW5vcmFtYSUyMGludGVyaW9yJTIwbW9kZXJuJTIwbHV4dXJ5JTIwbG9iYnklMjByb29tJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4OTAyNjE3Nnww&ixlib=rb-4.1.0&q=85&w=3600&h=1800&fit=crop";

function Counter({ target, suffix, testId }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const dur = 1800;
        const start = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            setVal(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, target]);

    const display = target % 1 !== 0 ? val.toFixed(1) : Math.round(val).toLocaleString("en-IN");
    return (
        <span ref={ref} data-testid={testId} className="text-3xl font-extrabold text-white sm:text-4xl">
            {display}
            <span className="text-[#00C6FF]">{suffix}</span>
        </span>
    );
}

export default function Showcase() {
    return (
        <section className="relative py-20 lg:py-32" data-testid="showcase-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHead
                    chapter="06"
                    eyebrow="Motion Showcase"
                    title="SEE IT. SPIN IT. EXPLORE IT."
                    sub="Experience your business like never before with an interactive 360° walkthrough."
                    align="center"
                />
                <Reveal delay={0.15} className="mt-12">
                    <div className="relative">
                        <SceneTour
                            testIdPrefix="showcase-tour"
                            heightClass="h-[58vh] min-h-[400px]"
                        />
                        <div className="pointer-events-none absolute left-1/2 top-[70px] z-10 -translate-x-1/2">
                            <span className="font-mono-accent rounded-full border border-[#00C6FF]/40 bg-[#050B14]/70 px-6 py-2.5 text-xs uppercase tracking-[0.4em] text-[#00C6FF] backdrop-blur-md">
                                Explore Every Angle
                            </span>
                        </div>
                    </div>
                </Reveal>

                <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {[
                        { target: 500, suffix: "+", label: "Spaces Captured", id: "stat-spaces" },
                        { target: 99.8, suffix: "%", label: "Client Satisfaction", id: "stat-satisfaction" },
                        { target: 10, suffix: "M+", label: "Virtual Visits Enabled", id: "stat-visits" },
                    ].map((s, i) => (
                        <motion.div
                            key={s.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            className="glass-card rounded-2xl p-8 text-center"
                        >
                            <Counter target={s.target} suffix={s.suffix} testId={s.id} />
                            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-slate-400">
                                {s.label}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
